import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiCall from '../../../../Redux/ApiCalls/AuthorizedApi';
import { getQuotationByIdAPI, shareQuotationOnWhatsAppAPI } from '../../../../Redux/ApiCalls/Dashboard/QuotationAPI';
import { getOrderByIdAPI, shareOrderOnWhatsAppAPI } from '../../../../Redux/ApiCalls/Dashboard/OrderAPI';
import { updateLeadStageAPI } from '../../../../Redux/ApiCalls/Dashboard/LeadsAPI';
import { showErrorToast, showSuccessToast } from '../../../atoms/Utlis/Toast';
import { exportElementAsPDF } from '../../../../Utils/pdfHelpers';

export interface Lead {
  id: string;
  phone: string;
}

export interface Quotation {
  id: string;
  quotation_no: string;
  customer_phone: string;
  [key: string]: any;
}

export interface Order {
  id: string;
  order_no: string;
  customer_phone: string;
  [key: string]: any;
}


const generatePdfBlob = async (element: HTMLElement): Promise<Blob> => {
  return exportElementAsPDF(element);
};

const normalizePhone = (phone?: string): string => phone?.replace(/\D/g, '') || '';

const isSamePhone = (phone1?: string, phone2?: string): boolean => {
  const p1 = normalizePhone(phone1);
  const p2 = normalizePhone(phone2);
  if (!p1 || !p2) return false;
  if (p1 === p2) return true;
  if (p1.length >= 10 && p2.length >= 10) {
    return p1.slice(-10) === p2.slice(-10);
  }
  return false;
};

export const useDocumentSharing = (onSuccess: () => void) => {
  const navigate = useNavigate();
  const [sharingQuotationLeadId, setSharingQuotationLeadId] = useState<string | null>(null);
  const [sharingOrderLeadId, setSharingOrderLeadId] = useState<string | null>(null);
  const [quotationActionMode, setQuotationActionMode] = useState<'send' | 'update'>('send');

  const [activeQuotation, setActiveQuotation] = useState<Quotation | null>(null);
  const [tempQuotation, setTempQuotation] = useState<Quotation | null>(null);
  const [quotationsSelectionList, setQuotationsSelectionList] = useState<Quotation[]>([]);
  const [noQuotationModal, setNoQuotationModal] = useState<{ open: boolean; lead: Lead | null }>({ open: false, lead: null });
  const [showQuotationSelectModal, setShowQuotationSelectModal] = useState(false);
  const [showQuotationVerifyModal, setShowQuotationVerifyModal] = useState(false);

  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [tempOrder, setTempOrder] = useState<Order | null>(null);
  const [ordersSelectionList, setOrdersSelectionList] = useState<Order[]>([]);
  const [noOrderModal, setNoOrderModal] = useState<{ open: boolean; lead: Lead | null }>({ open: false, lead: null });
  const [showOrderSelectModal, setShowOrderSelectModal] = useState(false);
  const [showOrderVerifyModal, setShowOrderVerifyModal] = useState(false);

  const printRef = useRef<HTMLDivElement>(null);
  const orderPrintRef = useRef<HTMLDivElement>(null);

  const searchAndSelectDocument = async <T extends { id: string; customer_phone?: string }>({
    lead,
    documentType,
    urlPath,
    getByIdAPI,
    setSelectionList,
    setShowSelectModal,
    setTempDocument,
    setShowVerifyModal,
    setNoDocModal,
    setSharingLeadId,
    onSingleMatch,
  }: {
    lead: Lead;
    documentType: 'quotation' | 'order';
    urlPath: string;
    getByIdAPI: (id: string) => Promise<T>;
    setSelectionList: (list: T[]) => void;
    setShowSelectModal: (show: boolean) => void;
    setTempDocument: (doc: T) => void;
    setShowVerifyModal: (show: boolean) => void;
    setNoDocModal: (state: { open: boolean; lead: Lead }) => void;
    setSharingLeadId: (id: string | null) => void;
    onSingleMatch?: (item: T) => Promise<void> | void;
  }) => {
    const searchPhone = lead.phone?.trim();
    if (!searchPhone) {
      showErrorToast('Customer phone number is missing.');
      return;
    }
    setSharingLeadId(lead.id);
    try {
      const rawDigits = normalizePhone(searchPhone);
      const querySearch = rawDigits.length >= 10 ? rawDigits.slice(-10) : searchPhone;

      const response = await apiCall({
        method: 'GET',
        url: `${urlPath}?search=${encodeURIComponent(querySearch)}`,
      });
      const list = response?.results || [];
      const matching = list.filter(
        (item: T) =>
          isSamePhone(item.customer_phone, searchPhone) ||
          isSamePhone((item as any).customer_details?.phone, searchPhone) ||
          isSamePhone((item as any).phone, searchPhone)
      );

      if (matching.length === 0) {
        setNoDocModal({ open: true, lead });
        setSharingLeadId(null);
        return;
      }

      if (matching.length > 1) {
        setSelectionList(matching);
        setShowSelectModal(true);
      } else {
        if (onSingleMatch) {
          await onSingleMatch(matching[0]);
        } else {
          const fullDoc = await getByIdAPI(matching[0].id);
          setTempDocument(fullDoc);
          setShowVerifyModal(true);
        }
      }
    } catch (err: any) {
      showErrorToast(err?.message || `Failed to search ${documentType}.`);
      setSharingLeadId(null);
    }
  };

  const handleSendQuotation = async (lead: Lead) => {
    setQuotationActionMode('send');
    await searchAndSelectDocument({
      lead,
      documentType: 'quotation',
      urlPath: '/quotation/quotations/',
      getByIdAPI: getQuotationByIdAPI,
      setSelectionList: setQuotationsSelectionList,
      setShowSelectModal: setShowQuotationSelectModal,
      setTempDocument: setTempQuotation,
      setShowVerifyModal: setShowQuotationVerifyModal,
      setNoDocModal: setNoQuotationModal,
      setSharingLeadId: setSharingQuotationLeadId,
    });
  };

  const handleUpdateQuotation = async (lead: Lead) => {
    setQuotationActionMode('update');
    await searchAndSelectDocument({
      lead,
      documentType: 'quotation',
      urlPath: '/quotation/quotations/',
      getByIdAPI: getQuotationByIdAPI,
      setSelectionList: setQuotationsSelectionList,
      setShowSelectModal: setShowQuotationSelectModal,
      setTempDocument: setTempQuotation,
      setShowVerifyModal: setShowQuotationVerifyModal,
      setNoDocModal: setNoQuotationModal,
      setSharingLeadId: setSharingQuotationLeadId,
      onSingleMatch: (q) => {
        setSharingQuotationLeadId(null);
        navigate(`/dashboard/quotation/singleQuotation?id=${q.id}`, { state: { fromLeads: true } });
      },
    });
  };

  const handleSendOrder = async (lead: Lead) => {
    await searchAndSelectDocument({
      lead,
      documentType: 'order',
      urlPath: '/order/orders/',
      getByIdAPI: getOrderByIdAPI,
      setSelectionList: setOrdersSelectionList,
      setShowSelectModal: setShowOrderSelectModal,
      setTempDocument: setTempOrder,
      setShowVerifyModal: setShowOrderVerifyModal,
      setNoDocModal: setNoOrderModal,
      setSharingLeadId: setSharingOrderLeadId,
    });
  };

  const handleConfirmSendQuotation = () => {
    setActiveQuotation(tempQuotation);
    setShowQuotationVerifyModal(false);
  };

  const handleCancelSendQuotation = () => {
    setTempQuotation(null);
    setShowQuotationVerifyModal(false);
    setSharingQuotationLeadId(null);
  };

  const handleConfirmSendOrder = () => {
    setActiveOrder(tempOrder);
    setShowOrderVerifyModal(false);
  };

  const handleCancelSendOrder = () => {
    setTempOrder(null);
    setShowOrderVerifyModal(false);
    setSharingOrderLeadId(null);
  };

  const generateAndSendDocument = async ({
    leadId,
    documentType,
    documentId,
    documentNo,
    customerPhone,
    element,
    shareAPI,
    targetStage,
    onComplete,
    onCleanup,
  }: {
    leadId: string;
    documentType: 'Quotation' | 'Order';
    documentId: string;
    documentNo: string;
    customerPhone: string;
    element: HTMLElement | null;
    shareAPI: (id: string, blob: Blob, filename: string) => Promise<any>;
    targetStage: 'QUOTED' | 'BOOKED';
    onComplete?: () => void;
    onCleanup: () => void;
  }) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (!element) throw new Error(`${documentType} print template not ready`);

      const blob = await generatePdfBlob(element);
      const filename = `${documentType}_${documentNo}.pdf`;

      const result = await shareAPI(documentId, blob, filename);

      await updateLeadStageAPI(leadId, { stage: targetStage });

      showSuccessToast(
        `${documentType} sent to ${result.sent_to || customerPhone} on WhatsApp 🎉`
      );

      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      showErrorToast(err?.message || `Failed to share ${documentType.toLowerCase()} on WhatsApp.`);
    } finally {
      onCleanup();
    }
  };

  useEffect(() => {
    if (!activeQuotation || !sharingQuotationLeadId) return;

    generateAndSendDocument({
      leadId: sharingQuotationLeadId,
      documentType: 'Quotation',
      documentId: activeQuotation.id,
      documentNo: activeQuotation.quotation_no,
      customerPhone: activeQuotation.customer_phone,
      element: printRef.current,
      shareAPI: shareQuotationOnWhatsAppAPI,
      targetStage: 'QUOTED',
      onCleanup: () => {
        setActiveQuotation(null);
        setSharingQuotationLeadId(null);
      },
    });
  }, [activeQuotation, sharingQuotationLeadId]);

  useEffect(() => {
    if (!activeOrder || !sharingOrderLeadId) return;

    generateAndSendDocument({
      leadId: sharingOrderLeadId,
      documentType: 'Order',
      documentId: activeOrder.id,
      documentNo: activeOrder.order_no,
      customerPhone: activeOrder.customer_phone,
      element: orderPrintRef.current,
      shareAPI: shareOrderOnWhatsAppAPI,
      targetStage: 'BOOKED',
      onComplete: onSuccess,
      onCleanup: () => {
        setActiveOrder(null);
        setSharingOrderLeadId(null);
      },
    });
  }, [activeOrder, sharingOrderLeadId, onSuccess]);

  const handleSelectQuotation = async (q: Quotation) => {
    if (quotationActionMode === 'update') {
      setShowQuotationSelectModal(false);
      setSharingQuotationLeadId(null);
      navigate(`/dashboard/quotation/singleQuotation?id=${q.id}`, { state: { fromLeads: true } });
      return;
    }
    try {
      const fullQuotation = await getQuotationByIdAPI(q.id);
      setTempQuotation(fullQuotation);
      setShowQuotationSelectModal(false);
      setShowQuotationVerifyModal(true);
    } catch {
      showErrorToast('Failed to fetch quotation details.');
    }
  };

  const handleSelectOrder = async (o: Order) => {
    try {
      const fullOrder = await getOrderByIdAPI(o.id);
      setTempOrder(fullOrder);
      setShowOrderSelectModal(false);
      setShowOrderVerifyModal(true);
    } catch {
      showErrorToast('Failed to fetch order details.');
    }
  };

  const sharingLeadId = sharingQuotationLeadId || sharingOrderLeadId;
  const setSharingLeadId = (id: string | null) => {
    setSharingQuotationLeadId(id);
    setSharingOrderLeadId(id);
  };

  return {
    sharingLeadId,
    activeQuotation,
    activeOrder,
    tempQuotation,
    tempOrder,
    quotationsSelectionList,
    ordersSelectionList,
    noQuotationModal,
    setNoQuotationModal,
    noOrderModal,
    setNoOrderModal,
    showQuotationSelectModal,
    setShowQuotationSelectModal,
    showOrderSelectModal,
    setShowOrderSelectModal,
    showQuotationVerifyModal,
    setShowQuotationVerifyModal,
    showOrderVerifyModal,
    setShowOrderVerifyModal,
    handleSendQuotation,
    handleUpdateQuotation,
    handleSendOrder,
    handleConfirmSendQuotation,
    handleCancelSendQuotation,
    handleConfirmSendOrder,
    handleCancelSendOrder,
    handleSelectQuotation,
    handleSelectOrder,
    setTempQuotation,
    setTempOrder,
    setSharingLeadId,
    printRef,
    orderPrintRef,
  };
};
